import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { shippedApps as apps } from "@/data/products";
import { FadeUp, Eyebrow, EASE } from "@/components/Reveal";

export const AppleMark = ({ className = "h-4 w-4 fill-current" }) => (
  <svg viewBox="0 0 384 512" className={className} aria-hidden="true">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.7-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

function StatusChip({ app, t }) {
  const map = { available: null, updated: t("products.updated"), coming: t("products.comingSoon") };
  const label = app.badge === "Flagship" ? t("products.flagship") : map[app.status];
  if (!label) return null;
  const coming = app.status === "coming";
  return (
    <span
      data-testid={`status-${app.slug}`}
      className={`rounded-full border px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] ${
        coming ? "border-ink/20 text-ink/50" : "border-cobalt/25 bg-cobalt/5 text-cobalt"
      }`}
    >
      {label}
    </span>
  );
}

function Card({ app, t, lang, localizePath, reduce, span = "md:col-span-6" }) {
  return (
    <FadeUp className={span} amount={0.2}>
      <motion.article
        whileHover={reduce ? undefined : { y: -6 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="group relative flex h-full flex-col border border-ink/10 bg-white transition-[border-color,box-shadow] duration-500 hover:border-ink/25 hover:shadow-[0_30px_60px_-30px_rgba(17,17,16,0.25)]"
      >
        <Link to={localizePath(`/app/${app.slug}`)} data-testid={`product-card-${app.slug}`} aria-label={app.name} className="absolute inset-0 z-0" />

        <div className="pointer-events-none flex items-center justify-between px-7 pt-7 md:px-9 md:pt-9">
          <span className="font-sans text-sm font-light text-ink/35">{app.category === "games" ? "G1" : app.slug === "icd10-on-the-go-2026" ? "01" : null}</span>
          <StatusChip app={app} t={t} />
        </div>

        <div className="pointer-events-none mx-7 mt-6 h-64 overflow-hidden border border-ink/10 bg-white md:mx-9">
          <img
            src={app.screenshots[0]}
            alt={`${app.name} screenshot`}
            loading="lazy"
            className={`h-full w-full ${app.slug === "icd10-2026" ? "object-contain" : "object-cover object-left-top"} transition-transform duration-700 ease-out group-hover:scale-[1.04]`}
          />
        </div>

        <div className="pointer-events-none flex flex-1 flex-col px-7 pb-8 pt-6 md:px-9">
          <div className="flex items-center gap-4">
            <img
              src={app.icon}
              alt=""
              className="h-12 w-12 shrink-0 rounded-[24%] border border-ink/10 shadow-[0_8px_16px_-8px_rgba(17,17,16,0.25)] md:h-14 md:w-14"
              loading="lazy"
            />
            <h3 className="flex items-center gap-2 text-2xl font-semibold leading-tight tracking-tight text-ink transition-colors duration-300 group-hover:text-cobalt md:text-[1.55rem]">
              {app.name}
              <ArrowUpRight className="h-5 w-5 shrink-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            </h3>
          </div>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/60">{lang === "es" ? app.cardLineEs : app.cardLine}</p>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <span className="flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-ink/40">
              {app.pricing && (
                <>
                  <span data-testid={`pricing-label-${app.slug}`} className="text-ink/55">{lang === "es" ? app.pricing.es : app.pricing.en}</span>
                  <span className="h-px w-4 bg-ink/20" aria-hidden="true" />
                </>
              )}
              <span>{app.compatibility}</span>
            </span>
            {app.storeUrl ? (
              <a
                href={app.storeUrl}
                target="_blank"
                rel="noreferrer"
                data-testid={`app-store-badge-${app.slug}`}
                className="pointer-events-auto relative z-10 inline-flex items-center gap-2.5 rounded-lg bg-ink px-4 py-2 text-white transition-colors duration-300 hover:bg-cobalt"
              >
                <AppleMark />
                <span className="leading-none">
                  <span className="block text-[8px] uppercase tracking-wider opacity-80">{t("products.downloadOn")}</span>
                  <span className="block text-[13px] font-semibold">{t("products.store")}</span>
                </span>
              </a>
            ) : (
              <span data-testid={`coming-soon-${app.slug}`} className="inline-flex items-center gap-2 rounded-lg border border-ink/15 px-4 py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-ink/50">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-cobalt/70" aria-hidden="true" />
                {t("products.comingSoon")}
              </span>
            )}
          </div>
        </div>
      </motion.article>
    </FadeUp>
  );
}

export default function AvailableNow() {
  const { t, lang, localizePath } = useLang();
  const reduce = useReducedMotion();
  const professional = apps.filter((a) => a.category === "professional");
  const shipped = professional.filter((a) => a.status !== "coming");
  const games = apps.filter((a) => a.category === "games");

  return (
    <section id="products" data-testid="available-now" className="bg-paper py-24 md:py-32">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <FadeUp><Eyebrow>{t("products.eyebrow")}</Eyebrow></FadeUp>
            <FadeUp delay={0.1}>
              <h2 className="mt-6 text-4xl font-semibold leading-[1.05] tracking-tight text-ink md:text-6xl">
                {t("products.title")}
              </h2>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <p className="font-sans text-6xl font-light text-cobalt/90 md:text-7xl" aria-label={`${professional.length} professional apps`}>{String(professional.length).padStart(2, "0")}</p>
          </FadeUp>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
          {shipped.map((app) => (
            <Card key={app.slug} app={app} t={t} lang={lang} localizePath={localizePath} reduce={reduce} />
          ))}
        </div>


        {games.length > 0 && <>
        <div className="mt-24 flex flex-wrap items-end justify-between gap-6 border-t border-ink/10 pt-16">
          <FadeUp>
            <h2 className="text-4xl font-semibold leading-[1.05] tracking-tight text-ink md:text-5xl">{t("products.gamesTitle")}</h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="font-sans text-5xl font-light text-cobalt/90 md:text-6xl" aria-label={`${games.length} games`}>{String(games.length).padStart(2, "0")}</p>
          </FadeUp>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-12">
          {games.map((app) => (
            <Card key={app.slug} app={app} t={t} lang={lang} localizePath={localizePath} reduce={reduce} span="md:col-span-12" />
          ))}
        </div>
        </>}
      </div>
    </section>
  );
}
